import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  Tooltip,
  Divider,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  TrendingUp,
  MouseOutlined,
  Lightbulb,
  ContentCopy,
  OpenInNew,
  PriorityHigh,
  ArrowUpward,
  EmojiObjects,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useSiteStore } from '../../store/site.store';
import { getStrikingDistance } from '../../services/strikingDistanceService';
import { getCtrAnomalies } from '../../services/ctrAnomalyService';
import { intentGapService } from '../../services/intentGapService';
import { getCannibalizationIssues } from '../../services/cannibalizationService';

// ─── Types ────────────────────────────────────────────────────────────────────

type Priority = 'HIGH' | 'MEDIUM' | 'LOW';
type SignalType = 'striking-distance' | 'ctr-anomaly' | 'intent-gap' | 'cannibalization';

interface Recommendation {
  id: string;
  priority: Priority;
  signal: SignalType;
  page: string;
  query: string;
  contributingQueries?: string[];
  evidenceLabel?: string;
  evidence?: string;
  implementationSteps?: string[];
  dateRange?: string;
  action: string;          // Short action label, e.g. "Improve title & meta description"
  why: string;             // 1-sentence explanation of the problem
  impact: string;          // Estimated impact phrased for a customer
  impactValue: number;     // Numeric value used for sorting
}

const formatDateRange = (dateFrom: string | null | undefined, dateTo: string | null | undefined) => {
  if (!dateFrom || !dateTo) return undefined;

  const from = new Date(dateFrom);
  const to = new Date(dateTo);

  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
    return `${dateFrom} to ${dateTo}`;
  }

  return `${from.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} to ${to.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
};

const PRIORITY_RANK: Record<Priority, number> = {
  HIGH: 0,
  MEDIUM: 1,
  LOW: 2,
};

const combinePriority = (current: Priority | undefined, next: Priority): Priority => {
  if (!current) return next;
  return PRIORITY_RANK[next] < PRIORITY_RANK[current] ? next : current;
};

const summarizeQueries = (queries: Array<string | null | undefined>, fallback: string) => {
  const unique = Array.from(new Set(queries.map((query) => query?.trim()).filter((query): query is string => !!query)));
  if (unique.length === 0) return fallback;
  const preview = unique.slice(0, 3).map((query) => `"${truncate(query, 40)}"`).join(', ');
  return unique.length > 3 ? `${preview} + ${unique.length - 3} more` : preview;
};

const uniqueQueries = (queries: Array<string | null | undefined>) =>
  Array.from(new Set(queries.map((query) => query?.trim()).filter((query): query is string => !!query)));

// ─── Signal → Recommendation transformers ────────────────────────────────────

function fromStrikingDistance(items: ReturnType<typeof getStrikingDistance> extends Promise<infer T> ? T : never): Recommendation[] {
  const grouped = new Map<string, { recommendation: Recommendation; queries: string[] }>();

  items.slice(0, 30).forEach((item) => {
    const key = item.page;
    const priority = item.rankingDifficulty === 'easy' ? 'HIGH' : item.rankingDifficulty === 'medium' ? 'MEDIUM' : 'LOW';
    const existing = grouped.get(key);

    if (!existing) {
      grouped.set(key, {
        recommendation: {
          id: `sd-${item.page}`,
          priority,
          signal: 'striking-distance',
          page: item.page,
          query: item.query,
          contributingQueries: item.query ? [item.query] : [],
          evidenceLabel: 'Supporting queries',
          evidence: item.query,
          dateRange: formatDateRange(item.dateFrom, item.dateTo),
          action: 'Strengthen on-page relevance for this page',
          why: `This page is already close to page 1 for at least one valuable query. Improving the page copy, headings, and internal linking could move more of those searches into the top results.`,
          impact: `+${(item.potentialClicks ?? 0).toLocaleString()} potential clicks/period if this page moves onto page 1.`,
          implementationSteps: [
            'Align page title and H1 with the primary customer intent.',
            'Expand the section that directly answers the top supporting queries.',
            'Add 2-3 internal links from related pages using clear anchor text.',
          ],
          impactValue: item.potentialClicks ?? 0,
        },
        queries: item.query ? [item.query] : [],
      });
      return;
    }

    const totalImpact = existing.recommendation.impactValue + (item.potentialClicks ?? 0);
    const queries = item.query ? [...existing.queries, item.query] : existing.queries;
    const dedupedQueries = uniqueQueries(queries);
    const allQueries = summarizeQueries(queries, 'Multiple striking-distance queries support this page.');
    const totalDateRange = existing.recommendation.dateRange ?? formatDateRange(item.dateFrom, item.dateTo);

    grouped.set(key, {
      recommendation: {
        ...existing.recommendation,
        priority: combinePriority(existing.recommendation.priority, priority),
        query: dedupedQueries[0] ?? existing.recommendation.query,
        contributingQueries: dedupedQueries,
        evidence: allQueries,
        dateRange: totalDateRange,
        why: `This page is sitting just outside page 1 for multiple queries with meaningful search demand. Improving on-page relevance here creates one clear action for the customer while addressing several ranking opportunities at once.`,
        impact: `Up to +${totalImpact.toLocaleString()} combined clicks/period if this page improves for the supporting queries.`,
        implementationSteps: [
          'Align page title and H1 with the primary customer intent.',
          'Expand the section that directly answers the top supporting queries.',
          'Add 2-3 internal links from related pages using clear anchor text.',
        ],
        impactValue: totalImpact,
      },
      queries,
    });
  });

  return Array.from(grouped.values(), (entry) => entry.recommendation);
}

function fromCtrAnomalies(items: ReturnType<typeof getCtrAnomalies> extends Promise<infer T> ? T : never): Recommendation[] {
  const grouped = new Map<string, { recommendation: Recommendation; queries: string[] }>();

  items.slice(0, 30).forEach((item) => {
    const key = item.page;
    const priority = item.isSignificant ? 'HIGH' : 'MEDIUM';
    const existing = grouped.get(key);

    if (!existing) {
      grouped.set(key, {
        recommendation: {
          id: `ctr-${item.page}`,
          priority,
          signal: 'ctr-anomaly',
          page: item.page,
          query: item.query ?? '(all queries)',
          contributingQueries: item.query ? [item.query] : [],
          evidenceLabel: 'Supporting queries',
          evidence: item.query ?? '(all queries)',
          dateRange: formatDateRange(item.dateFrom, item.dateTo),
          action: 'Rewrite the page title and meta description',
          why: `This page is already appearing prominently in search results, but its click-through rate is below expectation for its ranking position. Improving the title and meta description gives the customer one clear change to make on the page.`,
          impact: `Up to +${(item.potentialClicks ?? 0).toLocaleString()} extra clicks without changing rankings.`,
          implementationSteps: [
            'Rewrite the title to include the main user intent and a clear benefit.',
            'Update meta description with one compelling outcome and one call to action.',
            'Retest after the next indexing cycle and keep the better-performing variant.',
          ],
          impactValue: item.potentialClicks ?? 0,
        },
        queries: item.query ? [item.query] : [],
      });
      return;
    }

    const totalImpact = existing.recommendation.impactValue + (item.potentialClicks ?? 0);
    const queries = item.query ? [...existing.queries, item.query] : existing.queries;
    const dedupedQueries = uniqueQueries(queries);
    const allQueries = summarizeQueries(queries, 'Multiple CTR-anomaly queries support this page.');
    const totalDateRange = existing.recommendation.dateRange ?? formatDateRange(item.dateFrom, item.dateTo);

    grouped.set(key, {
      recommendation: {
        ...existing.recommendation,
        priority: combinePriority(existing.recommendation.priority, priority),
        query: dedupedQueries[0] ?? existing.recommendation.query,
        contributingQueries: dedupedQueries,
        evidence: allQueries,
        dateRange: totalDateRange,
        why: `Several search queries point to the same customer action here: the page is earning fewer clicks than it should for its current ranking. One title/meta rewrite can improve click appeal across all of those supporting queries.`,
        impact: `Up to +${totalImpact.toLocaleString()} combined extra clicks without any ranking change.`,
        implementationSteps: [
          'Rewrite the title to include the main user intent and a clear benefit.',
          'Update meta description with one compelling outcome and one call to action.',
          'Retest after the next indexing cycle and keep the better-performing variant.',
        ],
        impactValue: totalImpact,
      },
      queries,
    });
  });

  return Array.from(grouped.values(), (entry) => entry.recommendation);
}

function fromIntentGaps(items: ReturnType<typeof intentGapService.getIntentGapOpportunities> extends Promise<infer T> ? T : never): Recommendation[] {
  return items.slice(0, 30).map((item) => ({
    id: `ig-${item.id}`,
    priority: item.priorityScore >= 200 ? 'HIGH' : item.priorityScore >= 100 ? 'MEDIUM' : 'LOW',
    signal: 'intent-gap',
    page: item.url,
    query: item.query,
    contributingQueries: item.query ? [item.query] : [],
    action: item.suggestion,
    why: `People are searching "${item.query}" ${item.impressions?.toLocaleString()} times and your page comes up — but only ${(item.ctr * 100).toFixed(1)}% of them click through. This tells us the page doesn't give searchers what they're looking for. They want ${item.intentType}-style content and your current page doesn't deliver that, so they scroll past.`,
    impact: `Priority score ${item.priorityScore?.toFixed(0)} — addressing this intent could significantly improve organic engagement.`,
    implementationSteps: [
      'Add a section that directly addresses the searcher intent for this query.',
      'Use concise headings that match how customers phrase the problem.',
      'Include one actionable next step so visitors do not bounce.',
    ],
    impactValue: item.priorityScore ?? 0,
  }));
}

function fromCannibalization(items: ReturnType<typeof getCannibalizationIssues> extends Promise<infer T> ? T : never): Recommendation[] {
  return items.slice(0, 30).map((item) => ({
    id: `can-${item.id}`,
    priority: item.isSevere ? 'HIGH' : 'MEDIUM',
    signal: 'cannibalization',
    page: item.primaryPage ?? item.competingPages?.[0] ?? '—',
    query: item.query,
    contributingQueries: item.query ? [item.query] : [],
    action: `Consolidate ${item.pageCount} competing pages into one authoritative page`,
    why: `You have ${item.pageCount} different pages all trying to rank for "${item.query}". Google sees them as competitors and can't decide which one to promote, so it splits its ranking power between them — meaning none of them rank as well as they could. Think of it like having ${item.pageCount} salespeople pitch the same product to the same customer at the same time; it creates confusion instead of a clear winner.`,
    impact: `Consolidating will focus all link equity on one URL and can lift the combined ranking.`,
    implementationSteps: [
      'Select one primary page to own this query cluster.',
      'Merge or redirect overlapping pages into the primary page.',
      'Update internal links so they consistently point to the primary page.',
    ],
    impactValue: item.severityScore ?? 0,
  }));
}

// ─── UI helpers ───────────────────────────────────────────────────────────────

const PRIORITY_CONFIG: Record<Priority, { color: 'error' | 'warning' | 'info'; label: string; order: number }> = {
  HIGH:   { color: 'error',   label: 'High Priority',   order: 0 },
  MEDIUM: { color: 'warning', label: 'Medium Priority', order: 1 },
  LOW:    { color: 'info',    label: 'Low Priority',    order: 2 },
};

const SIGNAL_CONFIG: Record<SignalType, { label: string; icon: React.ReactNode; color: string }> = {
  'striking-distance': { label: 'Striking Distance', icon: <TrendingUp fontSize="small" />,   color: '#4caf50' },
  'ctr-anomaly':       { label: 'CTR Anomaly',       icon: <MouseOutlined fontSize="small" />, color: '#2196f3' },
  'intent-gap':        { label: 'Intent Gap',         icon: <Lightbulb fontSize="small" />,    color: '#ff9800' },
  'cannibalization':   { label: 'Cannibalization',    icon: <ContentCopy fontSize="small" />,  color: '#f44336' },
};

const truncate = (s: string, n = 60) => s?.length > n ? s.slice(0, n) + '…' : s;

const SummaryBadge: React.FC<{ count: number; signal: SignalType }> = ({ count, signal }) => {
  const cfg = SIGNAL_CONFIG[signal];
  return (
    <Card variant="outlined" sx={{ textAlign: 'center', p: 1.5 }}>
      <Box sx={{ color: cfg.color, mb: 0.5 }}>{cfg.icon}</Box>
      <Typography variant="h5" fontWeight={700} sx={{ color: cfg.color }}>{count}</Typography>
      <Typography variant="caption" color="text.secondary">{cfg.label}</Typography>
    </Card>
  );
};

type RecommendationViewMode = 'latest' | 'detail';

const RecommendationCard: React.FC<{ rec: Recommendation; rank: number; viewMode: RecommendationViewMode }> = ({ rec, rank, viewMode }) => {
  const pCfg = PRIORITY_CONFIG[rec.priority];
  const sCfg = SIGNAL_CONFIG[rec.signal];
  const isDetailView = viewMode === 'detail';
  const topQueries = (rec.contributingQueries && rec.contributingQueries.length > 0)
    ? rec.contributingQueries.slice(0, 5)
    : [rec.query];

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderLeft: 4,
        borderLeftColor: pCfg.color + '.main',
        '&:hover': { boxShadow: 2 },
        transition: 'box-shadow 0.15s',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
        {/* Rank */}
        <Box
          sx={{
            minWidth: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: 'grey.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Typography variant="caption" fontWeight={700} color="text.secondary">#{rank}</Typography>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Badges row */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            <Chip
              icon={<PriorityHigh fontSize="small" />}
              label={pCfg.label}
              size="small"
              color={pCfg.color}
              variant="filled"
              sx={{ fontWeight: 700 }}
            />
            <Chip
              icon={sCfg.icon as React.ReactElement}
              label={sCfg.label}
              size="small"
              variant="outlined"
              sx={{ borderColor: sCfg.color, color: sCfg.color }}
            />
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
            {isDetailView ? 'Recommended Action' : 'Latest Priority Action'}
          </Typography>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ mt: 0.25 }}>
            {rec.action}
          </Typography>

          {/* Page + query */}
          <Box sx={{ mb: 1 }}>
            <Tooltip title={rec.page}>
              <Box
                component="a"
                href={rec.page}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'primary.main',
                  textDecoration: 'none',
                  fontSize: '0.75rem',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {truncate(rec.page, 80)}
                <OpenInNew sx={{ fontSize: 12 }} />
              </Box>
            </Tooltip>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Primary query: <em>{truncate(rec.query, 120)}</em>
            </Typography>
            {isDetailView && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {(rec.evidenceLabel ?? 'Evidence summary')}: <em>{truncate(rec.evidence ?? rec.query, 120)}</em>
              </Typography>
            )}
            {rec.dateRange && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Analysis window: <em>{rec.dateRange}</em>
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 1 }} />

          {isDetailView && (
            <Box sx={{ mb: 1.25 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
                Top Contributing Queries
              </Typography>
              {topQueries.map((query, idx) => (
                <Typography key={`${rec.id}-query-${idx}`} variant="body2" color="text.primary" sx={{ mt: 0.25 }}>
                  {idx + 1}. {query}
                </Typography>
              ))}
            </Box>
          )}

          {/* Why + Impact */}
          <Grid container spacing={1}>
            {isDetailView && (
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
                  Why It Matters Now
                </Typography>
                <Typography variant="body2" color="text.primary" sx={{ mt: 0.25 }}>
                  {rec.why}
                </Typography>
                {!!rec.implementationSteps?.length && (
                <Box sx={{ mt: 1.25 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
                    Implementation Focus
                  </Typography>
                  {rec.implementationSteps.slice(0, 3).map((step, idx) => (
                    <Typography key={`${rec.id}-step-${idx}`} variant="body2" color="text.primary" sx={{ mt: 0.25 }}>
                      {idx + 1}. {step}
                    </Typography>
                  ))}
                </Box>
                )}
              </Grid>
            )}
            <Grid item xs={12} sm={isDetailView ? 6 : 12}>
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'success.main' }}>
                Business Value
              </Typography>
              <Typography variant="body2" color="success.dark" sx={{ mt: 0.25, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ArrowUpward sx={{ fontSize: 14 }} />
                {rec.impact}
              </Typography>
              {!isDetailView && (
                <Typography variant="body2" color="text.primary" sx={{ mt: 0.75 }}>
                  {truncate(rec.why, 180)}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Value score: {Math.round(rec.impactValue).toLocaleString()}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Paper>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const PRIORITY_ORDER: Record<Priority, number> = { HIGH: 0, MEDIUM: 1, LOW: 2 };

export const RecommendationsPanel: React.FC = () => {
  const { selectedSite } = useSiteStore();
  const siteId = selectedSite?.id;
  const [viewMode, setViewMode] = useState<RecommendationViewMode>('latest');

  const enabled = !!siteId;

  const { data: sdData = [],  isLoading: sdLoading  } = useQuery({ queryKey: ['striking-distance',  siteId], queryFn: () => getStrikingDistance(siteId!,  undefined, 50), enabled });
  const { data: ctrData = [], isLoading: ctrLoading } = useQuery({ queryKey: ['ctr-anomaly',         siteId], queryFn: () => getCtrAnomalies(siteId!,    undefined, 50), enabled });
  const { data: igData = [],  isLoading: igLoading  } = useQuery({ queryKey: ['intent-gaps',         siteId], queryFn: () => intentGapService.getIntentGapOpportunities(siteId!, { limit: 50 }), enabled });
  const { data: canData = [], isLoading: canLoading } = useQuery({ queryKey: ['cannibalization',     siteId], queryFn: () => getCannibalizationIssues(siteId!, undefined, 50), enabled });

  const isLoading = sdLoading || ctrLoading || igLoading || canLoading;

  // Merge and sort all recommendations: HIGH first, then by impact value desc
  const recommendations = useMemo<Recommendation[]>(() => {
    const all = [
      ...fromStrikingDistance(sdData),
      ...fromCtrAnomalies(ctrData),
      ...fromIntentGaps(igData),
      ...fromCannibalization(canData),
    ];
    return all.sort((a, b) => {
      const pDiff = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
      return pDiff !== 0 ? pDiff : b.impactValue - a.impactValue;
    });
  }, [sdData, ctrData, igData, canData]);

  const highCount   = recommendations.filter((r) => r.priority === 'HIGH').length;
  const mediumCount = recommendations.filter((r) => r.priority === 'MEDIUM').length;
  const lowCount    = recommendations.filter((r) => r.priority === 'LOW').length;

  if (!siteId) {
    return <Alert severity="info" sx={{ mt: 2 }}>Select a site to see recommendations.</Alert>;
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <EmojiObjects color="warning" />
          <Typography variant="h5" fontWeight={700}>
            Actionable Recommendations
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Prioritised actions derived from all four GSC signals — sorted by expected impact.
          Each recommendation is framed as a clear customer action with evidence and expected business value.
        </Typography>
        <Box sx={{ mt: 1.5 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={viewMode}
            onChange={(_, value) => {
              if (value) setViewMode(value);
            }}
          >
            <ToggleButton value="latest">Latest View</ToggleButton>
            <ToggleButton value="detail">Detail View</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {isLoading && (
        <Box>
          <LinearProgress sx={{ mb: 2 }} />
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }} color="text.secondary">Analysing signals across all data sources…</Typography>
          </Box>
        </Box>
      )}

      {!isLoading && (
        <>
          {/* Summary counts */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined" sx={{ textAlign: 'center', p: 1.5, borderColor: 'error.main' }}>
                <Typography variant="h4" fontWeight={700} color="error.main">{highCount}</Typography>
                <Typography variant="caption" color="text.secondary">High Priority</Typography>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined" sx={{ textAlign: 'center', p: 1.5, borderColor: 'warning.main' }}>
                <Typography variant="h4" fontWeight={700} color="warning.main">{mediumCount}</Typography>
                <Typography variant="caption" color="text.secondary">Medium Priority</Typography>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined" sx={{ textAlign: 'center', p: 1.5, borderColor: 'info.main' }}>
                <Typography variant="h4" fontWeight={700} color="info.main">{lowCount}</Typography>
                <Typography variant="caption" color="text.secondary">Low Priority</Typography>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card variant="outlined" sx={{ textAlign: 'center', p: 1.5 }}>
                <Typography variant="h4" fontWeight={700} color="text.primary">{recommendations.length}</Typography>
                <Typography variant="caption" color="text.secondary">Total Actions</Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Signal breakdown */}
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            {(['striking-distance', 'ctr-anomaly', 'intent-gap', 'cannibalization'] as SignalType[]).map((sig) => (
              <Grid item xs={6} sm={3} key={sig}>
                <SummaryBadge
                  signal={sig}
                  count={recommendations.filter((r) => r.signal === sig).length}
                />
              </Grid>
            ))}
          </Grid>

          {/* Recommendation list */}
          {recommendations.length === 0 ? (
            <Alert severity="success" icon={<EmojiObjects />}>
              No outstanding recommendations — all signals look healthy!
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {recommendations.map((rec, idx) => (
                <RecommendationCard key={rec.id} rec={rec} rank={idx + 1} viewMode={viewMode} />
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
